import React, { useMemo, useState } from "react";
import { DashboardLayout } from "./Dashboard";
import ProfileBanner from "@/components/ProfileBanner";
import { useNavigate, useParams } from "react-router";
import TableWithSearch from "@/components/TableWithSearch";
import { patientTableHeaders } from "@/utils/tablesData";
import { useOrg } from "@/contexts/useOrg";
import { Eye, History } from "lucide-react";
import { useGetData } from "@/hooks/useGetData";
import { Dialog } from "@/components/ui/dialog";
import MyDialogContent from "@/components/MyDialogContent";
import PatientHistoryTimeline from "@/components/patient/PatientHistoryTimeline";
import { HoverTooltip } from "@/components/ui/tooltip";
import RHFWrapper from "@/components/RHFWrapper";
import RhfInput from "@/components/rhfFields/RhfInput";
import { Button } from "@/components/ui/button";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import {
  capitalizeTheStartingCharacterFilter,
  PERSON_CAPITALIZE_FIELDS,
} from "@/constants/payloadFilters";
import { ResetSchema } from "./ResetPassword";
import { PractitionerForm } from "./Practitioners";
import LoaderWrapper from "./LoaderWrapper";

const PractitionerProfile = () => {
  const { id } = useParams();
  const [practitioner, setPractitioner] = useState<Record<string, any>>({});
  const [providers, setProviders] = useState([]);
  const [patients, setPatients] = React.useState([]);
  const navigate = useNavigate();
  const { selectedOrgId, organizations } = useOrg();
  const [password, setPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [editOpen, setEditOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const {
    fetchData: fetchPractitioner,
    loading: isLoading,
    err: practitionerErr,
  } = useGetData(
    "/practitioners/" + id,
    {},
    false,
    !!id,
    (err, data) => {
      if (data && !err) setPractitioner(data?.data ?? {});
    },
  );

  useGetData(
    "/providers",
    { orgId: selectedOrgId },
    false,
    !!selectedOrgId,
    (err, data) => {
      if (data && !err) {
        setProviders(data?.data?.providers);
      }
    },
  );

  const practitionerId = practitioner?._id || id;

  const filterOptions = useMemo(
    () => [
      {
        label: "Status",
        key: "status",
        isMulti: false,
        values: [
          { label: "Active", key: "active" },
          { label: "Inactive", key: "inactive" },
          { label: "Pending", key: "pending" },
          { label: "Blocked", key: "blocked" },
        ],
      },
    ],
    [],
  );

  const practitionerPatientsView = useMemo(
    () =>
      patients.map((patient: any) => ({
        ...patient,
        ...(patient?.caretakerMinutes ? patient?.caretakerMinutes : {}),
        firstName: `${patient?.firstName || ""} ${
          patient?.middleName || ""
        } ${patient?.lastName || ""}`.trim(),
        organizationId:
          patient?.organizationId?.organizationName || patient?.organizationId,
        assignedProviderId: patient?.assignedProviderId
          ? `${patient?.assignedProviderId?.firstName || ""} ${
              patient?.assignedProviderId?.middleName || ""
            } ${patient?.assignedProviderId?.lastName || ""}`.trim()
          : "",
        assignedCaretakerIds: Array.isArray(patient?.assignedCaretakerIds)
          ? patient?.assignedCaretakerIds
              .map(
                (caretaker: any) =>
                  `${caretaker?.firstName || ""} ${
                    caretaker?.middleName || ""
                  } ${caretaker?.lastName || ""}`.trim(),
              )
              .filter(Boolean)
              .join(", ")
          : "",
        heightValue: patient?.heightValue?.includes("_")
          ? `${patient.heightValue.split("_")[0]}' ${patient.heightValue.split("_")[1]}"`
          : patient?.heightValue,
      })),
    [patients],
  );

  const handlePasswordChange = async (formdata: any) => {
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      setLoading(true);
      await AxiosInstance().post("/auth/admin/reset-password", {
        entityId: practitioner?._id,
        password: formdata.password,
        role: "practitioner",
      });
      setPassword(false);
      resolveFn("Password changed successfully");
    } catch (error) {
      rejectFn(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePractitioner = async (formData: any, methods: any) => {
    const { pr, rejectFn, resolveFn } = getPromise();
    getPromiseToast(pr);
    try {
      setLoading(true);

      const updatedPractitioner = {
        firstName: formData?.firstName,
        middleName: formData?.middleName,
        lastName: formData?.lastName,
        dateOfBirth: formData?.dateOfBirth,
        email: formData?.email,
        phone: formData?.phone,
        street: formData?.street,
        city: formData?.city,
        state: formData?.state,
        zipcode: formData?.zipcode,
        gender: formData?.gender,
        status: formData?.status,
        timezone: formData?.timezone,
        organizationId: formData?.organizationId,
        providerId: formData?.providerId,
        npiNumber: formData?.npiNumber,
        specializations: formData?.specializations,
        taxonomies: formData?.taxonomies,
      };

      const { data }: any = await AxiosInstance().put(
        "/practitioners/" + practitioner?._id,
        capitalizeTheStartingCharacterFilter(
          updatedPractitioner,
          PERSON_CAPITALIZE_FIELDS,
        ),
      );

      setPractitioner(data?.data ?? {});
      await fetchPractitioner();

      resolveFn("Practitioner updated successfully");
      setEditOpen(false);
    } catch (error: any) {
      rejectFn(error);
      if (methods) {
        if (
          error?.response?.data?.message.includes("email") &&
          error?.response?.data?.message.includes("exist")
        ) {
          methods.setError("email", { message: "Email Already Exists" });
        }

        if (
          error?.response?.data?.message.includes("phone") &&
          error?.response?.data?.message.includes("exist")
        ) {
          methods.setError("phone", { message: "Phone Number Already Exists" });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout
      activePage="/practitioners"
      entityId={practitioner?.practitionerId || id}
      entityHeaderAfterId={
        practitioner?._id ? (
          <HoverTooltip content="Practitioner history" side="bottom">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-primary text-primary"
              aria-label="Practitioner history"
              onClick={() => setIsHistoryOpen(true)}
            >
              <History className="h-4 w-4" />
            </Button>
          </HoverTooltip>
        ) : null
      }
    >
      <LoaderWrapper loading={isLoading} error={practitionerErr}>
        <ProfileBanner
          accessModule="canAccessClinicians"
          updatePassword={() => {
            setPassword(true);
          }}
          profileData={practitioner}
          editProfile={() => setEditOpen(true)}
        />

        <TableWithSearch
          clickRow={() => {}}
          columnPreferenceKey={`practitioner-patients-${practitionerId}`}
          preferenceTableName="patient-list"
          apiDataKey="patients"
          url="/patients"
          columnHeaders={patientTableHeaders}
          data={patients}
          setData={setPatients}
          fetchCondition={true}
          searchPlaceholder="Search patients ..."
          heading="Patients List"
          isActionColumn
          handleAction={(data) => navigate("/patients/" + data?._id + "/edit")}
          isIncludeSearch
          params={{ orgId: selectedOrgId, practitionerId }}
          viewData={practitionerPatientsView}
          filtersOptions={filterOptions}
          filters={filters}
          setFilters={setFilters}
          isColumnSelection
          moreActions={[
            {
              label: "View ",
              icon: <Eye className="w-4 h-4" />,
              onClick: (data: any) => {
                navigate("/patients/" + data?._id);
              },
              isShow: () => true,
            },
          ]}
        />
      </LoaderWrapper>

      <Dialog open={password} onOpenChange={setPassword}>
        <MyDialogContent className="md:w-md" heading="Update Password">
          <RHFWrapper
            initialValues={{}}
            isValidate={true}
            onSubmit={handlePasswordChange}
            validateSchema={ResetSchema}
            className="space-y-4"
          >
            <RhfInput
              name="password"
              label="Password"
              type="password"
              placeholder="Password"
            />
            <RhfInput
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              placeholder="Confirm Password"
            />
            <Button type="submit" disabled={loading} className="w-full">
              Update
            </Button>
          </RHFWrapper>
        </MyDialogContent>
      </Dialog>

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
        }}
      >
        <MyDialogContent heading="Practitioner Details">
          <PractitionerForm
            key={practitioner?._id}
            btnloading={loading}
            className={"space-y-6"}
            facilities={[]}
            isEdit={true}
            defaultValues={practitioner}
            onClose={() => {
              setEditOpen(false);
            }}
            organizations={organizations}
            providers={providers}
            onSubmit={handleUpdatePractitioner}
          />
        </MyDialogContent>
      </Dialog>

      <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
        <MyDialogContent
          heading="Practitioner history"
          className="!h-[min(720px,85vh)] !w-[min(42rem,96vw)] !max-w-[42rem] [&>div]:!p-0 [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:right-5 [&_[data-slot=dialog-header]]:shrink-0 [&_[data-slot=dialog-header]]:border-b [&_[data-slot=dialog-header]]:border-[#E5E7EB] [&_[data-slot=dialog-header]]:!px-5 [&_[data-slot=dialog-header]]:!py-4 [&_[data-slot=dialog-title]]:pr-8 [&_[data-slot=dialog-title]]:text-base [&_[data-slot=dialog-title]]:font-semibold [&_[data-slot=dialog-title]]:text-[#111827] [&>div:last-child]:!flex [&>div:last-child]:!min-h-0 [&>div:last-child]:!flex-1 [&>div:last-child]:!flex-col [&>div:last-child]:!overflow-hidden [&>div:last-child]:!p-0 [&>div:last-child]:px-5 [&>div:last-child]:pb-4"
        >
          <PatientHistoryTimeline
            practitionerId={String(practitioner?._id ?? id ?? "")}
            enabled={isHistoryOpen}
          />
        </MyDialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default PractitionerProfile;
